import React from 'react';
import { Chip } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { FileCard } from './styles';
import { FileItem } from './types';
import { formatFileSize, formatDate, isCsvFile } from './utils';
import { useTranslation } from '../../i18n';
import FileCardBody from './FileCardBody';

const VERDICT_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  pass: 'success',
  warning: 'warning',
  fail: 'error',
  na: 'default',
};

interface DraggableFileCardProps {
  /**
   * The entry's identity (STD-029 R-2), resolved by `fileCardModels` and passed
   * in rather than computed here — so the "which row is this?" decision lives in
   * one tested place instead of inside a component no unit test can render.
   */
  entryId: string;
  file: FileItem;
  selected: boolean;
  /** Is this card cut to the clipboard? (renders dimmed) */
  cut: boolean;
  verdict?: string;
  /** Is this folder a valid drop target for the current drag? (gates the accept-highlight) */
  canDropHere: boolean;
  onItemClick: (file: FileItem, event: React.MouseEvent<HTMLElement>) => void;
  onItemOpen: (file: FileItem) => void;
  onContextMenu: (event: React.MouseEvent<HTMLElement>, file: FileItem) => void;
}

const DraggableFileCard: React.FC<DraggableFileCardProps> = ({
  entryId: id,
  file,
  selected,
  cut,
  verdict,
  canDropHere,
  onItemClick,
  onItemOpen,
  onContextMenu,
}) => {
  const { t } = useTranslation();

  // The dnd-kit id is the entry's identity, not its display path (STD-029 R-2).
  // Two rows at one display path would otherwise register as one draggable and
  // one droppable, and dnd-kit would resolve the drop to whichever registered
  // last. Nothing downstream parses this id: `handleDragEnd` reads the entry out
  // of `data` and takes the path from there.
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id,
    data: { file },
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id,
    data: { file },
    disabled: !file.isFolder,
  });

  const setRef = (node: HTMLElement | null) => {
    setDragRef(node);
    if (file.isFolder) setDropRef(node);
  };

  return (
    <FileCard
      ref={setRef}
      isFolder={file.isFolder}
      selected={selected}
      cut={cut}
      dropActive={isOver && canDropHere}
      data-testid="file-card"
      // Three attributes, three jobs, deliberately not merged. `data-entry-id`
      // is the identity the marquee hit-test selects by (R-2); `data-file-key`
      // is the display path, which is what existing browser specs address a card
      // by; `data-file-name` is the bare label, which is what a one-level
      // listing actually shows and what a spec inside a SHARED folder can use —
      // there the path is share-root-relative and a spec cannot know it.
      // Collapsing them would either hand the marquee paths again or make every
      // spec selector a uuid.
      data-entry-id={id}
      data-file-key={file.key}
      data-file-name={file.name}
      aria-selected={selected}
      sx={isDragging ? { opacity: 0.4 } : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onItemClick(file, e);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onItemOpen(file);
      }}
      onContextMenu={(e) => onContextMenu(e, file)}
      {...listeners}
      {...attributes}
    >
      {/* Appearance lives in FileCardBody, which the shared grid renders too, so
          a shared file cannot drift into looking like a different product
          (#1247). Everything this component adds is drag, drop and selection —
          none of which a shared entry has. */}
      <FileCardBody
        // The row's own `name`, not a slice of its display path. Since B-4 the
        // server sends it, so nothing has to guess where the last segment is —
        // and a folder's key no longer carries a trailing slash to strip.
        name={file.name}
        isFolder={!!file.isFolder}
        // A FOLDER now says what is in it: the count, and the drone whose logs
        // dominate it. Both come from `ChildEntry.aggregates`, computed over the
        // folder's whole subtree — and over the FILTERED subtree when a filter is
        // active, so the number agrees with what opening the folder shows.
        showMetaForFolder={!!file.isFolder && !!file.folderMetadata}
        primaryMeta={
          file.isFolder
            ? file.folderMetadata?.primarySerial
              ? t('fileManagement.folderSerial', { serial: file.folderMetadata.primarySerial })
              : ''
            : isCsvFile(file.name)
              ? file.flight_date
                ? formatDate(file.flight_date)
                : t('fileManagement.testData')
              : ''
        }
        secondaryMeta={
          file.isFolder
            ? file.folderMetadata
              ? t('fileManagement.folderFileCount', { count: file.folderMetadata.fileCount })
              : ''
            : formatFileSize(file.size)
        }
        extraMeta={
          verdict ? (
            <Chip
              label={verdict.toUpperCase()}
              color={VERDICT_COLORS[verdict] ?? 'default'}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
            />
          ) : undefined
        }
        onMore={(e) => onContextMenu(e, file)}
      />
    </FileCard>
  );
};

export default DraggableFileCard;
